import { useDispatch } from 'react-redux'
import { forwardRef, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import Icon from '@/components/icon'
import { createModal, useModalize } from '@/components/modalize'
import VerifiedBadge from '@/components/verified-badge'
import { BlueClock, Warning } from '@/components/icons'
import { breakpoints, colors, fontWeights } from '@/styles'

const Wrapper = styled.div`
  background: ${colors.white};
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  max-height: 98vh;
  max-width: 767px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-height: unset;
    border-radius: 16px 16px 0 0;
  }
`

const ModalClose = styled.div`
  position: absolute;
  top: 19px;
  right: 25px;
  z-index: 1;

  svg {
    cursor: pointer;
    margin: 0;
  }

  :hover {
    opacity: 0.7;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: 20px;
    right: 20px;
  }
`

const AboutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  background-color: ${colors.white};
  padding: 24px;
`

const ProfileTitle = styled.h3`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: ${fontWeights.bold};
  border-bottom: 1px solid ${colors.grey};
  padding: 26px 50px 18px 24px;
  width: 100%;
`

const IconWrapper = styled.div`
  margin-right: 12px;
`

const StyledBlueClock = styled(BlueClock)`
  margin-right: 6px;
`

const PendingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: ${fontWeights.medium};
  font-size: 12px;
  background-color: ${colors.blue96};
  color: ${colors.blue32};
  padding: 4px 8px;
  margin-bottom: 8px;
  border-radius: 8px;
  width: 100%;
  max-width: 152px;
`

const Text = styled.span`
  color: ${colors.grey16};
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  line-height: 20px;
  width: 100%;
`

const Subtitle = styled.span`
  color: ${colors.black};
  font-weight: ${fontWeights.medium};
  font-size: 16px;
`

const MultiParagraphContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;
  width: 100%;
  margin-top: ${props => (props.top ? props.top : 0)}px;
`

const StepsContainer = styled.div`
  margin-top: 32px;
`

const Link = styled.a`
  color: ${colors.link};
  text-decoration: underline;
`

const VerifyActionText = styled.span`
  color: ${colors.link};
  text-decoration: underline;
  cursor: pointer;
`

const TenantVerificationModal = forwardRef(({ verificationStatus, timelineRoomId }, ref) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [requestingVerification, setRequestingVerification] = useState(false)

  const handleRequestIdVerification = async () => {
    if (requestingVerification) return
    setRequestingVerification(true)
    const body = await dispatch.chat.requestIdVerification({
      timelineRoomId
    })
    if (body?.response?.ok) {
      setRequestingVerification(false)
    }
    close()
  }
  const isPending = verificationStatus === 'pending'
  const isVerified = verificationStatus === 'verified'
  const parsedVerificationStatus = isPending || isVerified ? verificationStatus : 'unverified'

  const texts = {
    unverified: {
      title: t(
        'l.chat.view.unverified_verification_modal.title',
        'This renter does not have a verified account.'
      ),
      bodyText: t(
        'l.chat.view.unverified_verification_modal_body.text',
        'We have not verified the identity of this user. Please proceed with caution.'
      ),
      stepText1: (
        <Trans i18nKey="l.chat.view.unverified_verification_modal_step1.text">
          1. Request that they{' '}
          <VerifyActionText onClick={handleRequestIdVerification}>
            verify their profile
          </VerifyActionText>
          .
        </Trans>
      ),
      stepText2: (
        <Trans i18nKey="l.chat.view.unverified_verification_modal_step2.text">
          2. Learn more about our{' '}
          <Link target="_blank" href="https://liv.rent/blog/2019/10/rental-verification-process/">
            verification process
          </Link>
          .
        </Trans>
      )
    },
    pending: {
      title: t(
        'l.chat.view.pending_verification_modal.title',
        'This renter does not have a verified account.'
      ),
      bodyText: t(
        'l.chat.view.pending_verification_modal_body.text',
        'This user has already submitted their ID, but is pending verification.'
      ),
      stepText1: t(
        'l.chat.view.pending_verification_modal_step1.text',
        '1. Check back within 2-3 business days.'
      ),
      stepText2: (
        <Trans i18nKey="l.chat.view.pending_verification_modal_step2.text">
          2. Learn more about our{' '}
          <Link target="_blank" href="https://liv.rent/blog/2019/10/rental-verification-process/">
            verification process
          </Link>
          .
        </Trans>
      )
    },
    verified: {
      title: t(
        'l.chat.view.verified_verification_modal.title',
        'This renter has been ID verified.'
      ),
      bodyText1: t(
        'l.chat.view.verified_verification_modal_bodytext1.text',
        'We have verified that this renter has provided a government-issued ID and a selfie that matches their profile information.'
      ),
      bodyText2: (
        <Trans i18nKey="l.chat.view.verified_verification_modal_bodytext2.text">
          Our verification process is in place to ensure that landlords like you can find trusted
          tenants. Learn more about our{' '}
          <Link target="_blank" href="https://liv.rent/blog/2019/10/rental-verification-process/">
            verification process
          </Link>
          .
        </Trans>
      )
    }
  }

  return (
    <Wrapper ref={ref}>
      <ModalClose>
        <Icon aria-label="Close" id="close" width={24} height={24} onClick={close} />
      </ModalClose>
      <ProfileTitle>
        <IconWrapper>
          {!isVerified ? (
            <Warning />
          ) : (
            <VerifiedBadge type="id" width={24} height={24} verified={true} />
          )}
        </IconWrapper>
        <span>{texts?.[parsedVerificationStatus].title}</span>
      </ProfileTitle>

      <AboutWrapper>
        {isPending && (
          <PendingContainer>
            <StyledBlueClock />
            <span>{t('l.chat.view.pending_verification.text', 'Pending Verification')}</span>
          </PendingContainer>
        )}
        {isVerified ? (
          <MultiParagraphContainer>
            <Text>{texts.verified.bodyText1}</Text>
            <Text>{texts.verified.bodyText2}</Text>
          </MultiParagraphContainer>
        ) : (
          <Text>{texts?.[parsedVerificationStatus].bodyText}</Text>
        )}
        {!isVerified && (
          <StepsContainer>
            <Subtitle>{t('l.chat.view.steps_title.title', 'What you can do')}:</Subtitle>
            <MultiParagraphContainer top={16}>
              <Text>{texts?.[parsedVerificationStatus].stepText1}</Text>
              <Text>{texts?.[parsedVerificationStatus].stepText2}</Text>
            </MultiParagraphContainer>
          </StepsContainer>
        )}
      </AboutWrapper>
    </Wrapper>
  )
})

TenantVerificationModal.displayName = 'TenantVerificationModal'

export const showTenantVerificationModal = createModal(TenantVerificationModal, {
  width: 'auto',
  maxWidth: '496px',
  flexGrow: true,
  border: '1px solid #d8d8d8',
  mobileAlign: 'flex-end',
  tabletFullWidth: true
})
